import React, { useState, forwardRef, useImperativeHandle } from "react";
import VetStartedSOAPSection from "./VetStartedSOAPSection";

// ─────────────────────────────────────────────────────────────────────────────

const FieldLabel = ({ children }) => (
  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
    {children}
  </p>
);

const NoteField = ({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
}) => (
  <div>
    {label && <FieldLabel>{label}</FieldLabel>}

    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full text-[12px] text-slate-700 border border-slate-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent placeholder-slate-300 bg-white leading-relaxed"
    />
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const Assessment = forwardRef(({
  defaultOpen = false,
  isExpanded = true,
  onToggle,
  onDirtyChange,
  initialData, // NEW
}, ref) => {
  const [diagnosis, setDiagnosis] = useState(initialData?.diagnosis || "");
  const [differentialDiagnosis, setDifferentialDiagnosis] = useState(
    Array.isArray(initialData?.differentialsList)
      ? initialData.differentialsList.join("\n")
      : ""
  );
  const [clinicalNotes, setClinicalNotes] = useState(initialData?.clinicalNotes || "");
  const [severity, setSeverity] = useState(initialData?.severity || "");
  const [prognosis, setPrognosis] = useState(initialData?.prognosis || "");

  // Fires only from real user actions below — never from mount/render.
  const markDirty = () => onDirtyChange?.(true);

  const handleDiagnosisChange = (val) => {
    setDiagnosis(val);
    markDirty();
  };

  const handleDifferentialChange = (val) => {
    setDifferentialDiagnosis(val);
    markDirty();
  };

  const handleClinicalNotesChange = (val) => {
    setClinicalNotes(val);
    markDirty();
  };

  const handleSeverityChange = (val) => {
    setSeverity(val);
    markDirty();
  };

  const handlePrognosisChange = (val) => {
    setPrognosis(val);
    markDirty();
  };

  // NEW: expose current Assessment data to the parent via ref
  useImperativeHandle(ref, () => ({
    getData: () => ({
      diagnosis,
      differentialDiagnosis,
      clinicalNotes,
      severity,
      prognosis,
    }),
  }));

  const answeredCount =
    (diagnosis ? 1 : 0) +
    (differentialDiagnosis ? 1 : 0) +
    (clinicalNotes ? 1 : 0) +
    (severity ? 1 : 0) +
    (prognosis ? 1 : 0);

  return (
    <VetStartedSOAPSection
      id="soap-section-A"
      letter="A"
      title="Assessment"
      subtitle="Diagnosis, differentials & prognosis"
      accentBg="#fffbeb"
      accentColor="#b45309"
      badge={answeredCount > 0 ? `${answeredCount} items` : null}
      defaultOpen={defaultOpen}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {/* Diagnosis + Differential Diagnosis */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <NoteField
          label="Primary Diagnosis"
          value={diagnosis}
          onChange={handleDiagnosisChange}
          placeholder="Enter confirmed or working diagnosis..."
          rows={4}
        />

        <NoteField
          label="Differential Diagnosis"
          value={differentialDiagnosis}
          onChange={handleDifferentialChange}
          placeholder={`Example:
            Pancreatitis
            Foreign Body Obstruction
            Kidney Disease`}
          rows={4}
        />
      </div>

      {/* Severity + Prognosis */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FieldLabel>Severity</FieldLabel>

          <select
            value={severity}
            onChange={(e) => handleSeverityChange(e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-[12px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
          >
            <option value="">Select severity</option>
            <option value="Mild">Mild</option>
            <option value="Moderate">Moderate</option>
            <option value="Severe">Severe</option>
            <option value="Critical">Critical</option>
          </select>
        </div>

        <div>
          <FieldLabel>Prognosis</FieldLabel>

          <select
            value={prognosis}
            onChange={(e) => handlePrognosisChange(e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-3 py-2 text-[12px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
          >
            <option value="">Select prognosis</option>
            <option value="Excellent">Excellent</option>
            <option value="Good">Good</option>
            <option value="Fair">Fair</option>
            <option value="Guarded">Guarded</option>
            <option value="Poor">Poor</option>
          </select>
        </div>
      </div>

      {/* Clinical Notes */}
      <div className="mt-3">
        <NoteField
          label="Clinical Impression / Notes"
          value={clinicalNotes}
          onChange={handleClinicalNotesChange}
          placeholder="Assessment summary, reasoning, findings..."
          rows={4}
        />
      </div>
    </VetStartedSOAPSection>
  );
});

Assessment.displayName = "Assessment";

export default Assessment;